import React, { useState, useEffect, useMemo } from 'react';
import { 
  Box, 
  Card, 
  CardContent, 
  Typography, 
  TextField, 
  Button, 
  Select, 
  MenuItem, 
  FormControl, 
  InputLabel, 
  OutlinedInput,
  InputAdornment,
  IconButton,
  Divider,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Menu,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  Chip,
  Alert,
  SelectChangeEvent
} from '@mui/material';
import { 
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ContentCopy as DuplicateIcon,
  MoreVert as MoreIcon,
  FilterAlt as FilterIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../store';
import { Formulation, FormulationSummary } from '../../types/formulation';
import {
  fetchFormulations,
  fetchFormulationById,
  deleteFormulation,
  searchFormulations,
  setSelectedFormulationId,
  clearSelectedFormulation
} from '../../store/slices/formulationSlice';
// Define debounce function inline to avoid import issues
const debounce = <T extends (...args: any[]) => any>(
  func: T,
  wait: number
): ((...args: Parameters<T>) => void) => {
  let timeout: ReturnType<typeof setTimeout> | null = null;
  
  return (...args: Parameters<T>): void => {
    const later = () => {
      timeout = null;
      func(...args);
    };
    
    if (timeout !== null) {
      clearTimeout(timeout);
    }
    
    timeout = setTimeout(later, wait);
  };
};

// Available crop types for selection - should match backend CROP_TYPES
const CROP_TYPES = ['Tomato', 'Lettuce', 'Strawberry', 'Cucumber', 'Pepper', 'Herbs', 'Microgreens', 'Custom'];

interface FormulationSelectorV2Props {
  onSelect?: (formulation: Formulation) => void;
  onEdit?: (formulation: Formulation) => void;
  onDuplicate?: (formulation: Formulation) => void;
  showControls?: boolean;
}

/**
 * Enhanced component for selecting, managing, and previewing formulations using V2 API
 */
const FormulationSelectorV2: React.FC<FormulationSelectorV2Props> = ({
  onSelect,
  onEdit,
  onDuplicate,
  showControls = true,
}) => {
  // Component state
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [cropTypeFilter, setCropTypeFilter] = useState<string>('all');
  const [showFilters, setShowFilters] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [actionFormulationId, setActionFormulationId] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  
  // Redux state - using formulationV2 slice
  const dispatch = useDispatch();
  const { 
    formulations, 
    selectedFormulation, 
    loading, 
    error, 
    deletingFormulation 
  } = useSelector((state: RootState) => state.formulation);

  // Find the formulation for the action menu
  const actionFormulation = useMemo(() => {
    return actionFormulationId 
      ? formulations.find(f => f.id === actionFormulationId) 
      : null;
  }, [actionFormulationId, formulations]);

  // Fetch formulations on component mount
  useEffect(() => {
    dispatch(fetchFormulations() as any);
  }, [dispatch]);

  // Create a debounced search function
  const debouncedSearch = useMemo(() => 
    debounce((term: string, cropType: string) => {
      // If both filters are empty/default, fetch all
      if (!term.trim() && cropType === 'all') {
        dispatch(fetchFormulations() as any);
        return;
      }
      
      // Otherwise use search endpoint with filters
      dispatch(searchFormulations(term.trim()) as any);
    }, 300),
  [dispatch]);

  // Handle search input change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newSearchTerm = e.target.value;
    setSearchTerm(newSearchTerm);
    debouncedSearch(newSearchTerm, cropTypeFilter);
  };

  // Handle crop type filter change
  const handleCropTypeChange = (e: SelectChangeEvent<string>) => {
    const newCropType = e.target.value;
    setCropTypeFilter(newCropType);
    debouncedSearch(searchTerm, newCropType);
  };

  // Handle formulation selection
  const handleFormulationSelect = (formulationId: string) => {
    dispatch(fetchFormulationById(formulationId) as any);
  };

  // Handle action menu open
  const handleActionMenuOpen = (event: React.MouseEvent<HTMLButtonElement>, formulationId: string) => {
    setActionFormulationId(formulationId);
    setAnchorEl(event.currentTarget);
  };
  
  // Handle action menu close
  const handleActionMenuClose = () => {
    setAnchorEl(null);
  };
  
  // Handle edit action
  const handleEdit = () => {
    handleActionMenuClose();
    if (actionFormulation && selectedFormulation && onEdit) {
      onEdit(selectedFormulation);
    }
  };
  
  // Handle duplicate action
  const handleDuplicate = () => {
    handleActionMenuClose();
    if (actionFormulation && selectedFormulation && onDuplicate) {
      onDuplicate(selectedFormulation);
    }
  };
  
  // Handle delete confirmation dialog open
  const handleDeleteDialogOpen = () => {
    handleActionMenuClose();
    setDeleteDialogOpen(true);
  };
  
  // Handle delete confirmation dialog close
  const handleDeleteDialogClose = () => {
    setDeleteDialogOpen(false);
  };
  
  // Handle delete action
  const handleDelete = () => {
    if (actionFormulationId) {
      dispatch(deleteFormulation(actionFormulationId) as any);
      handleDeleteDialogClose();
      if (selectedFormulation?.id === actionFormulationId) {
        dispatch(clearSelectedFormulation());
      }
    }
  };
  
  // Handle create new formulation
  const handleCreateNew = () => {
    if (onSelect) {
      // Pass an empty formulation to signal creating a new one
      onSelect({
        name: '',
        elements: [],
      });
    }
  };
  
  // Handle toggle filters
  const handleToggleFilters = () => {
    setShowFilters(!showFilters);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Search and filter section */}
      <Box sx={{ mb: 2 }}>
        <TextField
          placeholder="Search formulations..."
          fullWidth
          value={searchTerm}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={handleToggleFilters} size="small">
                  <FilterIcon />
                </IconButton>
                {showControls && (
                  <IconButton 
                    onClick={handleCreateNew}
                    color="primary"
                    size="small"
                  >
                    <AddIcon />
                  </IconButton>
                )}
              </InputAdornment>
            )
          }}
          variant="outlined"
          size="small"
        />
        
        {/* Filters section - shown when filters button is clicked */}
        {showFilters && (
          <Box sx={{ mt: 2, mb: 1, display: 'flex', alignItems: 'center' }}>
            <FormControl fullWidth size="small">
              <InputLabel id="crop-type-filter-label">Crop Type</InputLabel>
              <Select
                labelId="crop-type-filter-label"
                value={cropTypeFilter}
                onChange={handleCropTypeChange}
                label="Crop Type"
              >
                <MenuItem value="all">All Crop Types</MenuItem>
                {CROP_TYPES.map(type => (
                  <MenuItem key={type} value={type}>{type}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )}
      </Box>

      {/* Error handling */}
      {error && (
        <Alert 
          severity="error" 
          sx={{ mb: 2 }}
          action={
            <Button 
              color="inherit" 
              size="small"
              startIcon={<RefreshIcon />}
              onClick={() => dispatch(fetchFormulations() as any)}
            >
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      )}
      
      {/* Formulation list */}
      <Paper 
        variant="outlined" 
        sx={{ 
          flexGrow: 1, 
          overflow: 'auto',
          maxHeight: '400px',
          bgcolor: 'background.paper'
        }}
      >
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : formulations.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No formulations found.
              {searchTerm || cropTypeFilter !== 'all' ? ' Try adjusting your search or filters.' : ''}
            </Typography>
          </Box>
        ) : (
          <List disablePadding>            {formulations.map((formulation, index) => (
              <React.Fragment key={formulation.id || index}>
                <ListItem
                  sx={{ 
                    cursor: 'pointer',
                    backgroundColor: selectedFormulation?.id === formulation.id ? 'rgba(0, 0, 0, 0.08)' : 'transparent'
                  }}
                  onClick={() => handleFormulationSelect(formulation.id)}
                >
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {formulation.name}
                      </Box>
                    }
                    secondary={
                      <Box sx={{ mt: 0.5 }}>
                        {formulation.cropType && (
                          <Chip 
                            label={formulation.cropType} 
                            size="small" 
                            color="primary" 
                            variant="outlined"
                            sx={{ mr: 1, mb: 0.5 }}
                          />
                        )}
                        <Typography 
                          variant="body2" 
                          component="span" 
                          color="text.secondary"
                          sx={{ display: 'inline', verticalAlign: 'middle' }}
                        >
                          {formulation.description || 'No description'}
                          {formulation.targetEc && formulation.targetPh ? 
                            ` • EC: ${formulation.targetEc} mS/cm • pH: ${formulation.targetPh}` : ''}
                        </Typography>
                      </Box>
                    }
                  />
                  
                  {/* Action buttons */}
                  {showControls && (
                    <ListItemSecondaryAction>
                      <IconButton 
                        edge="end" 
                        aria-label="actions"
                        onClick={(e) => handleActionMenuOpen(e, formulation.id)}
                      >
                        <MoreIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  )}
                </ListItem>
                {index < formulations.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        )}
      </Paper>
      
      {/* Selected formulation details */}
      {selectedFormulation && (
        <Card sx={{ mt: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {selectedFormulation.name}
            </Typography>
            
            {selectedFormulation.description && (
              <Typography variant="body2" sx={{ mb: 1 }}>
                {selectedFormulation.description}
              </Typography>
            )}
            
            {selectedFormulation.cropType && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                <strong>Crop Type:</strong> {selectedFormulation.cropType}
              </Typography>
            )}

            <Box sx={{ mt: 1, display: 'flex', gap: 2 }}>
              {selectedFormulation.targetEc && (
                <Typography variant="body2">
                  <strong>Target EC:</strong> {selectedFormulation.targetEc} mS/cm
                </Typography>
              )}
              
              {selectedFormulation.targetPh && (
                <Typography variant="body2">
                  <strong>Target pH:</strong> {selectedFormulation.targetPh}
                </Typography>
              )}
            </Box>
            
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2">
                <strong>Elements:</strong> {selectedFormulation.elements?.length || 0} element targets
              </Typography>
            </Box>

            {showControls && (
              <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                {onEdit && (
                  <Button 
                    variant="outlined" 
                    size="small"
                    startIcon={<EditIcon />}
                    onClick={() => onEdit(selectedFormulation)}
                  >
                    Edit
                  </Button>
                )}
                
                {onDuplicate && (
                  <Button 
                    variant="outlined" 
                    size="small"
                    startIcon={<DuplicateIcon />}
                    onClick={() => onDuplicate(selectedFormulation)}
                  >
                    Duplicate
                  </Button>
                )}
              </Box>
            )}
          </CardContent>
        </Card>
      )}
      
      {/* Action menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleActionMenuClose}
      >
        {onEdit && (
          <MenuItem onClick={handleEdit}>
            <EditIcon fontSize="small" sx={{ mr: 1 }} />
            Edit
          </MenuItem>
        )}
        {onDuplicate && (
          <MenuItem onClick={handleDuplicate}>
            <DuplicateIcon fontSize="small" sx={{ mr: 1 }} />
            Duplicate
          </MenuItem>
        )}
        <MenuItem onClick={handleDeleteDialogOpen} sx={{ color: 'error.main' }}>
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
          Delete
        </MenuItem>
      </Menu>
      
      {/* Delete confirmation dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteDialogClose}
      >
        <DialogTitle>Delete Formulation</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete "{actionFormulation?.name}"? 
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteDialogClose}>Cancel</Button>
          <Button 
            onClick={handleDelete} 
            color="error" 
            disabled={deletingFormulation}
            startIcon={deletingFormulation ? <CircularProgress size={16} /> : null}
          >
            {deletingFormulation ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FormulationSelectorV2;
